import type { ID } from '@octopus-do/octopus-project-core'
import noop from 'lodash/noop'
import type React from 'react'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { getBlockContentText } from '@/utils'
import {
  FormInput,
  FormSelect,
  FormTextarea,
  OpenGraphImage,
  UpgradeInfo,
} from './components'
import css from './styles.module.css'
import { useSidebarForm } from './useSidebarForm'
import { getCounterText, getCounterTextClassnames } from './utils'

interface Props {
  nodeId: ID
}

export const SeoSidebarForm: React.FC<Props> = ({ nodeId }) => {
  const {
    form,
    slugError,
    readOnly,
    hasAccess,
    canFormEdit,
    handleOnChange,
    handleUrlBlur,
  } = useSidebarForm(nodeId)

  const slugCounterText = slugError.isValid
    ? getCounterText('slug', form.slug.length)
    : slugError.message

  return (
    <div className={css.seo_sidebar_form}>
      {!hasAccess && !readOnly && <UpgradeInfo />}

      {!readOnly && (
        <FormInput
          name="url"
          value={form.url}
          label="Page URL"
          placeholder="Enter link URL"
          canEdit={canFormEdit}
          readOnly={readOnly}
          cnClassName={css.seo_sidebar_form_field_wrap}
          onChange={(value) => {
            handleOnChange({ name: 'url', value })
          }}
          onBlur={() => {
            handleUrlBlur()
            sendGtmEvent(
              GtmTriggerName.PAGE_LINK,
              'Change page url in SEO mode'
            )
          }}
        />
      )}

      <OpenGraphImage
        image={form.image}
        labelClassName={css.seo_sidebar_form_label}
        nodeId={nodeId}
        readOnly={readOnly}
        canEdit={canFormEdit}
      />

      <FormInput
        name="title"
        value={form.title}
        label="Title"
        counterText={getCounterText('title', form.title.length)}
        canEdit={canFormEdit}
        readOnly={readOnly}
        cnClassName={css.seo_sidebar_form_field_wrap}
        counterClassName={getCounterTextClassnames('title', form.title.length)}
        onChange={(value) => {
          handleOnChange({ name: 'title', value })
        }}
        onBlur={() => {
          sendGtmEvent(GtmTriggerName.SEO_TITLE)
        }}
      />

      <FormTextarea
        name="description"
        label="Meta Description"
        value={form.description}
        canEdit={canFormEdit}
        counterText={getCounterText('description', form.description.length)}
        readOnly={readOnly}
        cnClassName={css.seo_sidebar_form_field_wrap}
        counterClassName={getCounterTextClassnames(
          'description',
          form.description.length
        )}
        onChange={(value) => {
          handleOnChange({ name: 'description', value })
        }}
        onBlur={() => {
          sendGtmEvent(GtmTriggerName.SEO_DESCRIPTION)
        }}
      />

      {!readOnly && (
        <FormInput
          name="slug"
          value={form.slug}
          label="Slug"
          counterText={slugCounterText}
          canEdit={canFormEdit}
          readOnly={readOnly}
          cnClassName={css.seo_sidebar_form_field_wrap}
          counterClassName={getCounterTextClassnames(
            'slug',
            form.slug.length,
            slugError.isValid
          )}
          onChange={(value) => {
            handleOnChange({ name: 'slug', value })
          }}
          onBlur={() => {
            sendGtmEvent(GtmTriggerName.SEO_SLUG)
          }}
        />
      )}

      <FormInput
        name="h1_header"
        value={form.h1_header}
        label="H1 Header"
        canEdit={canFormEdit}
        readOnly={readOnly}
        cnClassName={css.seo_sidebar_form_field_wrap}
        onChange={(value) => {
          handleOnChange({ name: 'h1_header', value })
        }}
        onBlur={() => {
          sendGtmEvent(GtmTriggerName.SEO_H1)
        }}
      />

      <FormInput
        name="keywords"
        value={form.keywords}
        label="Keywords"
        canEdit={canFormEdit}
        readOnly={readOnly}
        cnClassName={css.seo_sidebar_form_field_wrap}
        onChange={(value) => {
          handleOnChange({ name: 'keywords', value })
        }}
        onBlur={() => {
          sendGtmEvent(GtmTriggerName.SEO_KEYWORD)
        }}
      />

      <FormSelect
        name="pageIntent"
        label="Page intent"
        value={form.pageIntent}
        canEdit={canFormEdit}
        readOnly={readOnly}
        cnClassName={css.seo_sidebar_form_field_wrap}
        onChange={(value) => {
          handleOnChange({ name: 'pageIntent', value })
          sendGtmEvent(GtmTriggerName.SEO_INTENT)
        }}
      />

      {!readOnly && (
        <FormTextarea
          name="note"
          label="Additional Tags"
          value={getBlockContentText(form.note)}
          canEdit={canFormEdit}
          readOnly={readOnly}
          cnClassName={css.seo_sidebar_form_field_wrap}
          onChange={(value) => {
            handleOnChange({ name: 'note', value })
          }}
          onBlur={noop}
        />
      )}
    </div>
  )
}
